import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Avatar,
  Chip,
  TextField,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ArrowForward as ArrowBackIcon, // In RTL, ArrowForward points back
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Restaurant as RestaurantIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { toJalaali, toGregorian } from 'jalaali-js';
import * as XLSX from 'xlsx';
import PersianDatePicker from '../../components/PersianDatePicker';
import {
  reportsAPI,
  UserReportResponse,
  UserReservationItem,
  getImageUrl,
} from '../../services/api';
import { toPersianNumber, formatPersianNumber } from '../../utils/persianNumber';

type SortField = 'mealDate' | 'mealTitle' | 'price' | 'staffShare' | 'companyShare' | 'isDelivered' | 'createdAt';
type SortOrder = 'asc' | 'desc';

const UserReport: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<UserReportResponse | null>(null);
  const [error, setError] = useState<string>('');

  // Filters
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [mealType, setMealType] = useState<string>('all');
  const [isCorporate, setIsCorporate] = useState<string>('all');
  const [isDelivered, setIsDelivered] = useState<string>('all');
  const [isCancelled, setIsCancelled] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Sorting & Pagination
  const [sortField, setSortField] = useState<SortField>('mealDate');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const fetchUserReport = async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const response = await reportsAPI.getUserReport(userId, {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        mealType: mealType !== 'all' ? mealType : undefined,
        isCorporate: isCorporate !== 'all' ? isCorporate : undefined,
        isDelivered: isDelivered !== 'all' ? isDelivered : undefined,
        isCancelled: isCancelled !== 'all' ? isCancelled : undefined,
      });
      setData(response);
    } catch (err: any) {
      console.error('Failed to load user report:', err);
      setError('خطا در دریافت گزارش کاربر. لطفاً مجدداً تلاش نمایید.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserReport();
  }, [userId, startDate, endDate, mealType, isCorporate, isDelivered, isCancelled]);

  const handleQuickDate = (period: 'all' | 'thisMonth' | 'pastMonth' | 'past3Months') => {
    const now = new Date();
    if (period === 'all') {
      setStartDate('');
      setEndDate('');
      return;
    }

    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    setEndDate(todayStr);

    if (period === 'thisMonth') {
      const jNow = toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate());
      const gFirst = toGregorian(jNow.jy, jNow.jm, 1);
      const sStr = `${gFirst.gy}-${String(gFirst.gm).padStart(2, '0')}-${String(gFirst.gd).padStart(2, '0')}`;
      setStartDate(sStr);
    } else if (period === 'pastMonth') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      setStartDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    } else if (period === 'past3Months') {
      const d = new Date();
      d.setDate(d.getDate() - 90);
      setStartDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
  };

  // Convert Gregorian YYYY-MM-DD to Persian Date with Weekday safely
  const formatPersianDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    try {
      let year: number, month: number, day: number;
      if (typeof dateStr === 'string' && dateStr.includes('-')) {
        const parts = dateStr.split('T')[0].split('-').map(Number);
        year = parts[0];
        month = parts[1];
        day = parts[2];
      } else {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '-';
        year = d.getFullYear();
        month = d.getMonth() + 1;
        day = d.getDate();
      }
      if (!year || isNaN(year) || !month || isNaN(month) || !day || isNaN(day)) return '-';
      const date = new Date(year, month - 1, day);
      const j = toJalaali(year, month, day);
      const weekdays = ['یک‌شنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'];
      const weekday = weekdays[date.getDay()];
      return `${weekday} ${toPersianNumber(j.jy)}/${toPersianNumber(String(j.jm).padStart(2, '0'))}/${toPersianNumber(String(j.jd).padStart(2, '0'))}`;
    } catch {
      return String(dateStr);
    }
  };

  const getMealTypeLabel = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'lunch':
        return 'ناهار';
      case 'dinner':
        return 'شام';
      case 'breakfast':
        return 'صبحانه';
      case 'snack':
        return 'میان‌وعده';
      default:
        return type || '-';
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Local filtering (Search query) & Sorting
  const filteredAndSortedReservations = useMemo(() => {
    if (!data?.reservations) return [];

    let list = [...data.reservations];

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((r) =>
        (r.mealTitle && r.mealTitle.toLowerCase().includes(q)) ||
        (r.deliveryCode && r.deliveryCode.toLowerCase().includes(q)) ||
        (r.description && r.description.toLowerCase().includes(q)) ||
        (r.ratingComment && r.ratingComment.toLowerCase().includes(q))
      );
    }

    list.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      if (sortField === 'mealDate' || sortField === 'createdAt') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [data?.reservations, searchQuery, sortField, sortOrder]);

  const paginatedReservations = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedReservations.slice(start, start + rowsPerPage);
  }, [filteredAndSortedReservations, page, rowsPerPage]);

  const handleExportExcel = () => {
    if (!data || !filteredAndSortedReservations.length) return;

    const rows = filteredAndSortedReservations.map((r, index) => ({
      'ردیف': index + 1,
      'تاریخ وعده': formatPersianDate(r.mealDate),
      'وعده': getMealTypeLabel(r.mealType),
      'عنوان غذا': r.mealTitle || '-',
      'نوع غذا': r.isCorporate ? 'سازمانی' : 'آزاد',
      'قیمت کل (تومان)': r.price || 0,
      'سهم پرسنل (تومان)': r.staffShare || 0,
      'یارانه سازمان (تومان)': r.companyShare || 0,
      'وضعیت تحویل': r.isDelivered ? 'تحویل شده' : 'در انتظار تحویل',
      'زمان تحویل': r.deliveredAt ? formatPersianDate(r.deliveredAt) : '-',
      'وضعیت رزرو': r.isCancelled ? 'لغو شده' : 'معتبر',
      'کد تحویل': r.deliveryCode || '-',
      'امتیاز': r.rating ? `${r.rating} از ۵` : '-',
      'نظر کاربر': r.ratingComment || '-',
      'تاریخ ثبت رزرو': formatPersianDate(r.createdAt),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'رزروهای پرسنل');

    const fullName = `${data.user.firstName}_${data.user.lastName}`.replace(/\s+/g, '_');
    XLSX.writeFile(wb, `گزارش_رزروهای_${fullName}.xlsx`);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, direction: 'rtl', fontFamily: 'Vazir, sans-serif' }}>
      {/* Top Navigation & Actions Bar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon />}
            sx={{
              height: '48px',
              borderRadius: '12px',
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 600,
              px: 2.5,
            }}
          >
            بازگشت
          </Button>
          <Typography variant="h5" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
            گزارش تفصیلی رزروها و عملکرد پرسنل
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchUserReport}
            startIcon={<RefreshIcon />}
            sx={{
              height: '48px',
              borderRadius: '12px',
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 600,
            }}
          >
            بروزرسانی
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={handlePrint}
            startIcon={<PrintIcon />}
            sx={{
              height: '48px',
              borderRadius: '12px',
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 600,
            }}
          >
            چاپ
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={handleExportExcel}
            startIcon={<DownloadIcon />}
            disabled={!filteredAndSortedReservations.length}
            sx={{
              height: '48px',
              borderRadius: '12px',
              fontFamily: 'Vazir, sans-serif',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(46, 125, 50, 0.25)',
            }}
          >
            خروجی اکسل
          </Button>
        </Box>
      </Box>

      {/* User Information Card */}
      {data?.user && (
        <Card
          sx={{
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            mb: 3,
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: '#fff',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={getImageUrl(data.user.avatarUrl)}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: '#fff',
                  color: '#1976d2',
                  fontSize: '24px',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                {data.user.firstName ? data.user.firstName[0] : <PersonIcon />}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                  {data.user.firstName} {data.user.lastName}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', opacity: 0.9 }}>
                  {data.user.jobTitle || data.user.role === 'admin' ? 'مدیر سیستم' : 'پرسنل سازمانی'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={data.user.isActive ? 'حساب فعال' : 'حساب غیرفعال'}
                color={data.user.isActive ? 'success' : 'error'}
                sx={{
                  fontFamily: 'Vazir, sans-serif',
                  fontWeight: 700,
                  borderRadius: '8px',
                  bgcolor: data.user.isActive ? '#2e7d32' : '#d32f2f',
                  color: '#fff',
                }}
              />
              {data.user.workShift && (
                <Chip
                  label={`شیفت: ${data.user.workShift === 'day' ? 'روز' : 'شب'}`}
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    borderRadius: '8px',
                  }}
                />
              )}
            </Box>
          </Box>

          <CardContent sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BadgeIcon color="action" />
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    کد پرسنلی:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                    {data.user.personnelCode ? toPersianNumber(data.user.personnelCode) : '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon color="action" />
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    شماره موبایل:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                    {toPersianNumber(data.user.mobile.startsWith('0') ? data.user.mobile : '0' + data.user.mobile)}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="action" />
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    واحد / شرکت:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                    {[data.user.unitTitle, data.user.companyTitle || data.user.company].filter(Boolean).join(' - ') || '-'}
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCardIcon color="action" />
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    شماره کارت:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                    {data.user.cardNumber ? toPersianNumber(data.user.cardNumber) : '-'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Statistics / Summary KPI Cards */}
      {data?.summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif', mb: 1 }}>
                کل رزروها
              </Typography>
              <Typography variant="h5" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: 'primary.main' }}>
                {toPersianNumber(data.summary.totalReservations)}
              </Typography>
            </Card>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif', mb: 1 }}>
                سازمانی / آزاد
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                <span style={{ color: '#2e7d32' }}>{toPersianNumber(data.summary.corporateCount)}</span>
                {' / '}
                <span style={{ color: '#ed6c02' }}>{toPersianNumber(data.summary.freeCount)}</span>
              </Typography>
            </Card>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif', mb: 1 }}>
                تحویل شده / انتظار
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                <span style={{ color: '#1976d2' }}>{toPersianNumber(data.summary.deliveredCount)}</span>
                {' / '}
                <span style={{ color: '#9c27b0' }}>{toPersianNumber(data.summary.pendingDeliveryCount)}</span>
              </Typography>
            </Card>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', textAlign: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif', mb: 1 }}>
                رزروهای لغو شده
              </Typography>
              <Typography variant="h5" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: 'error.main' }}>
                {toPersianNumber(data.summary.cancelledCount)}
              </Typography>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Card
              sx={{
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                textAlign: 'center',
                p: 2,
                bgcolor: '#f1f8e9',
                border: '1px solid #c8e6c9',
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', mb: 0.5, color: '#2e7d32', fontWeight: 600 }}>
                سهم پرسنل (تومان)
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: '#1b5e20' }}>
                {formatPersianNumber(data.summary.totalStaffShare)}
              </Typography>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Card
              sx={{
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                textAlign: 'center',
                p: 2,
                bgcolor: '#e3f2fd',
                border: '1px solid #bbdefb',
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', mb: 0.5, color: '#1565c0', fontWeight: 600 }}>
                یارانه سازمان (تومان)
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: '#0d47a1' }}>
                {formatPersianNumber(data.summary.totalCompanyShare)}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter and Search Bar */}
      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', p: 2.5, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, mb: 2 }}>
          فیلترها و جستجوی پیشرفته
        </Typography>

        {/* Quick Date Shortcuts */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
          <Button
            size="small"
            variant={!startDate && !endDate ? 'contained' : 'outlined'}
            onClick={() => handleQuickDate('all')}
            sx={{ borderRadius: '12px', height: '36px', fontFamily: 'Vazir, sans-serif' }}
          >
            کل تاریخ‌ها
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleQuickDate('thisMonth')}
            sx={{ borderRadius: '12px', height: '36px', fontFamily: 'Vazir, sans-serif' }}
          >
            ماه جاری
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleQuickDate('pastMonth')}
            sx={{ borderRadius: '12px', height: '36px', fontFamily: 'Vazir, sans-serif' }}
          >
            ۳۰ روز گذشته
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleQuickDate('past3Months')}
            sx={{ borderRadius: '12px', height: '36px', fontFamily: 'Vazir, sans-serif' }}
          >
            ۳ ماه گذشته
          </Button>
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Start Date */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <PersianDatePicker
              label="از تاریخ"
              value={startDate}
              onChange={(val) => setStartDate(val)}
            />
          </Grid>

          {/* End Date */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <PersianDatePicker
              label="تا تاریخ"
              value={endDate}
              onChange={(val) => setEndDate(val)}
            />
          </Grid>

          {/* Meal Type */}
          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>وعده غذایی</InputLabel>
              <Select
                value={mealType}
                label="وعده غذایی"
                onChange={(e) => setMealType(e.target.value)}
                sx={{ height: '48px', borderRadius: '12px', fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="all" sx={{ fontFamily: 'Vazir, sans-serif' }}>همه وعده‌ها</MenuItem>
                <MenuItem value="lunch" sx={{ fontFamily: 'Vazir, sans-serif' }}>ناهار</MenuItem>
                <MenuItem value="dinner" sx={{ fontFamily: 'Vazir, sans-serif' }}>شام</MenuItem>
                <MenuItem value="breakfast" sx={{ fontFamily: 'Vazir, sans-serif' }}>صبحانه</MenuItem>
                <MenuItem value="snack" sx={{ fontFamily: 'Vazir, sans-serif' }}>میان‌وعده</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Corporate / Free */}
          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>نوع سفارش</InputLabel>
              <Select
                value={isCorporate}
                label="نوع سفارش"
                onChange={(e) => setIsCorporate(e.target.value)}
                sx={{ height: '48px', borderRadius: '12px', fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="all" sx={{ fontFamily: 'Vazir, sans-serif' }}>همه انواع</MenuItem>
                <MenuItem value="true" sx={{ fontFamily: 'Vazir, sans-serif' }}>سازمانی</MenuItem>
                <MenuItem value="false" sx={{ fontFamily: 'Vazir, sans-serif' }}>آزاد</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Delivery Status */}
          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>وضعیت تحویل</InputLabel>
              <Select
                value={isDelivered}
                label="وضعیت تحویل"
                onChange={(e) => setIsDelivered(e.target.value)}
                sx={{ height: '48px', borderRadius: '12px', fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="all" sx={{ fontFamily: 'Vazir, sans-serif' }}>همه</MenuItem>
                <MenuItem value="true" sx={{ fontFamily: 'Vazir, sans-serif' }}>تحویل شده</MenuItem>
                <MenuItem value="false" sx={{ fontFamily: 'Vazir, sans-serif' }}>در انتظار</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Cancellation Status */}
          <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>وضعیت لغو</InputLabel>
              <Select
                value={isCancelled}
                label="وضعیت لغو"
                onChange={(e) => setIsCancelled(e.target.value)}
                sx={{ height: '48px', borderRadius: '12px', fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="all" sx={{ fontFamily: 'Vazir, sans-serif' }}>همه</MenuItem>
                <MenuItem value="false" sx={{ fontFamily: 'Vazir, sans-serif' }}>معتبر (لغو نشده)</MenuItem>
                <MenuItem value="true" sx={{ fontFamily: 'Vazir, sans-serif' }}>لغو شده</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Text Search */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="جستجو در لیست..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                style: { height: '48px', borderRadius: '12px', fontFamily: 'Vazir, sans-serif' },
              }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
          </Grid>
        </Grid>
      </Card>

      {/* Main Reservations Table */}
      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="medium">
            <TableHead sx={{ bgcolor: '#f5f7fa' }}>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, width: 60, textAlign: 'center' }}>
                  ردیف
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortField === 'mealDate'}
                    direction={sortField === 'mealDate' ? sortOrder : 'asc'}
                    onClick={() => handleSort('mealDate')}
                  >
                    تاریخ وعده
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center' }}>
                  وعده
                </TableCell>
                {/* Per Meal Image Display Rule: meal image must be shown next to food title */}
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                  <TableSortLabel
                    active={sortField === 'mealTitle'}
                    direction={sortField === 'mealTitle' ? sortOrder : 'asc'}
                    onClick={() => handleSort('mealTitle')}
                  >
                    غذا
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center' }}>
                  نوع
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortField === 'price'}
                    direction={sortField === 'price' ? sortOrder : 'asc'}
                    onClick={() => handleSort('price')}
                  >
                    قیمت کل (تومان)
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortField === 'staffShare'}
                    direction={sortField === 'staffShare' ? sortOrder : 'asc'}
                    onClick={() => handleSort('staffShare')}
                  >
                    سهم پرسنل (تومان)
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center' }}>
                  <TableSortLabel
                    active={sortField === 'companyShare'}
                    direction={sortField === 'companyShare' ? sortOrder : 'asc'}
                    onClick={() => handleSort('companyShare')}
                  >
                    یارانه سازمان (تومان)
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center' }}>
                  وضعیت تحویل
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center' }}>
                  کد تحویل
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center' }}>
                  امتیاز / نظر
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" sx={{ mt: 2, fontFamily: 'Vazir, sans-serif', color: 'text.secondary' }}>
                      در حال دریافت گزارش رزروها...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedReservations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                    <RestaurantIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography variant="body1" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                      هیچ رزروی با مشخصات فیلتر شده یافت نشد
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      می‌توانید فیلترهای تاریخ یا وضعیت سفارش را تغییر دهید.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReservations.map((res, index) => {
                  const rowIndex = page * rowsPerPage + index + 1;
                  return (
                    <TableRow
                      key={res.id}
                      hover
                      sx={{
                        opacity: res.isCancelled ? 0.6 : 1,
                        bgcolor: res.isCancelled ? '#fff5f5' : 'inherit',
                      }}
                    >
                      {/* Row Index */}
                      <TableCell sx={{ fontFamily: 'Vazir, sans-serif', textAlign: 'center' }}>
                        {toPersianNumber(rowIndex)}
                      </TableCell>

                      {/* Meal Date */}
                      <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatPersianDate(res.mealDate)}
                      </TableCell>

                      {/* Meal Type */}
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={getMealTypeLabel(res.mealType)}
                          size="small"
                          sx={{
                            fontFamily: 'Vazir, sans-serif',
                            borderRadius: '6px',
                            fontWeight: 600,
                            bgcolor: '#f0f4f8',
                          }}
                        />
                      </TableCell>

                      {/* Food Title with Avatar image per Meal Image Display Rule */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={getImageUrl(res.mealImageUrl)}
                            alt={res.mealTitle}
                            variant="rounded"
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: '10px',
                              bgcolor: '#eee',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                            }}
                          >
                            <RestaurantIcon />
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, fontSize: '14px' }}>
                              {res.mealTitle}
                            </Typography>
                            {res.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: 'Vazir, sans-serif', display: 'block' }}
                              >
                                {res.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Corporate / Free */}
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Chip
                          label={res.isCorporate ? 'سازمانی' : 'آزاد'}
                          size="small"
                          sx={{
                            fontFamily: 'Vazir, sans-serif',
                            fontWeight: 700,
                            borderRadius: '8px',
                            bgcolor: res.isCorporate ? '#e8f5e9' : '#fff3e0',
                            color: res.isCorporate ? '#2e7d32' : '#e65100',
                          }}
                        />
                      </TableCell>

                      {/* Total Price */}
                      <TableCell sx={{ fontFamily: 'Vazir, sans-serif', textAlign: 'center', fontWeight: 600 }}>
                        {formatPersianNumber(res.price)}
                      </TableCell>

                      {/* Staff Share */}
                      <TableCell
                        sx={{
                          fontFamily: 'Vazir, sans-serif',
                          textAlign: 'center',
                          fontWeight: 700,
                          color: '#2e7d32',
                        }}
                      >
                        {formatPersianNumber(res.staffShare)}
                      </TableCell>

                      {/* Company Subsidy */}
                      <TableCell
                        sx={{
                          fontFamily: 'Vazir, sans-serif',
                          textAlign: 'center',
                          fontWeight: 700,
                          color: '#1565c0',
                        }}
                      >
                        {formatPersianNumber(res.companyShare)}
                      </TableCell>

                      {/* Delivery Status */}
                      <TableCell sx={{ textAlign: 'center' }}>
                        {res.isCancelled ? (
                          <Chip
                            icon={<CancelIcon fontSize="small" />}
                            label="لغو شده"
                            color="error"
                            size="small"
                            sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '8px' }}
                          />
                        ) : res.isDelivered ? (
                          <Tooltip
                            title={res.deliveredAt ? `تحویل شده در: ${formatPersianDate(res.deliveredAt)}` : 'تحویل شده'}
                            arrow
                          >
                            <Chip
                              icon={<CheckCircleIcon fontSize="small" />}
                              label="تحویل شده"
                              color="success"
                              size="small"
                              sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '8px' }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            icon={<PendingIcon fontSize="small" />}
                            label="در انتظار تحویل"
                            color="warning"
                            size="small"
                            sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '8px' }}
                          />
                        )}
                      </TableCell>

                      {/* Delivery Code */}
                      <TableCell sx={{ fontFamily: 'Vazir, sans-serif', textAlign: 'center', fontWeight: 700 }}>
                        {res.deliveryCode ? (
                          <Box
                            component="span"
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              bgcolor: '#f5f5f5',
                              border: '1px dashed #bdbdbd',
                              borderRadius: '6px',
                              letterSpacing: '1px',
                            }}
                          >
                            {toPersianNumber(res.deliveryCode)}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      {/* Rating & Comment */}
                      <TableCell sx={{ textAlign: 'center' }}>
                        {res.rating ? (
                          <Tooltip title={res.ratingComment || 'بدون نظر'} arrow>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}>
                              <StarIcon sx={{ color: '#faaf00', fontSize: 18 }} />
                              <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, fontSize: '13px' }}>
                                {toPersianNumber(res.rating)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredAndSortedReservations.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="تعداد در هر صفحه:"
          labelDisplayedRows={({ from, to, count }) =>
            `${toPersianNumber(from)}–${toPersianNumber(to)} از ${toPersianNumber(count !== -1 ? count : `بیش از ${to}`)}`
          }
          sx={{
            fontFamily: 'Vazir, sans-serif',
            borderTop: '1px solid #e0e0e0',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontFamily: 'Vazir, sans-serif',
            },
          }}
        />
      </Card>
    </Box>
  );
};

export default UserReport;
